import { contentAPI } from '../services/api';
import { toAssetCdnUrl, isS3ImageCdnConfigured } from './assetCdnUrl.js';
import { trackBrochureDownload } from './googleAnalytics.js';

async function errorMessageFromAxios(err) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const j = JSON.parse(text);
      return j?.message || null;
    } catch {
      return null;
    }
  }
  if (data && typeof data === 'object' && data.message) return String(data.message);
  return err?.message || null;
}

function filenameFromDisposition(disposition) {
  const d = String(disposition || '');
  const nameMatch = d.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  return nameMatch?.[1]?.trim() || null;
}

function triggerBlobDownload(blob, filename) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename || 'brochure.pdf';
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 2000);
}

function filenameFromUrl(url, fallbackName) {
  const fromPath = String(url || '')
    .split('?')[0]
    .split('/')
    .pop();
  if (fromPath && /\.pdf$/i.test(fromPath)) return fromPath;
  const label = String(fallbackName || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return label ? `${label}-invincible.pdf` : 'brochure-invincible.pdf';
}

/** Resolve brochure asset to CloudFront URL when CDN is configured. */
export function resolveBrochureCdnUrl(assetUrl) {
  const trimmed = String(assetUrl || '').trim();
  if (!trimmed) return '';
  if (!isS3ImageCdnConfigured()) return trimmed;
  const cdn = toAssetCdnUrl(trimmed);
  return cdn && !/amazonaws\.com/i.test(cdn) ? cdn : trimmed;
}

/**
 * Open a blank tab synchronously during the user click (before any await).
 * Required so browsers do not block the brochure open after async work.
 * Do NOT use "noopener" here — we need to set location after the API call.
 */
export function openBrochurePlaceholderWindow() {
  try {
    return window.open('about:blank', '_blank');
  } catch {
    return null;
  }
}

function assignBrochureWindow(popup, cdnUrl) {
  if (!cdnUrl) return false;
  if (popup && !popup.closed) {
    try {
      popup.opener = null;
      popup.location.href = cdnUrl;
      return true;
    } catch {
      try {
        popup.close();
      } catch {
        /* ignore */
      }
    }
  }
  // Fallback when popup was blocked or unavailable
  const a = document.createElement('a');
  a.href = cdnUrl;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  return true;
}

/**
 * Download trek brochure from CloudFront.
 * Pass `popup` from openBrochurePlaceholderWindow() called sync on click.
 */
export async function downloadBrochureFromUrl(assetUrl, { name, popup } = {}) {
  const trimmed = String(assetUrl || '').trim();
  if (!trimmed) {
    if (popup && !popup.closed) {
      try {
        popup.close();
      } catch {
        /* ignore */
      }
    }
    return false;
  }

  const cdnUrl = resolveBrochureCdnUrl(trimmed);
  const filename = filenameFromUrl(cdnUrl || trimmed, name);
  trackBrochureDownload(name || filename || 'Trek Brochure');

  // Preferred: navigate pre-opened tab (or open CDN URL). No CORS fetch.
  if (cdnUrl && !/amazonaws\.com/i.test(cdnUrl)) {
    assignBrochureWindow(popup, cdnUrl);
    return true;
  }

  // Legacy: API streams or 302s when CDN env is missing on the frontend build.
  try {
    const res = await contentAPI.downloadBrochureAsset(cdnUrl || trimmed, { name });
    if (popup && !popup.closed) {
      try {
        popup.close();
      } catch {
        /* ignore */
      }
    }
    const blob = new Blob([res.data], {
      type: res.headers['content-type'] || 'application/pdf'
    });
    const fromHeader = filenameFromDisposition(res.headers['content-disposition']);
    triggerBlobDownload(blob, fromHeader || filename);
    return true;
  } catch (e) {
    try {
      const apiBase = String(import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
      if (apiBase) {
        const q = new URLSearchParams({
          url: trimmed,
          ...(name ? { name: String(name) } : {})
        });
        assignBrochureWindow(popup, `${apiBase}/content/brochure-download?${q.toString()}`);
        return true;
      }
    } catch {
      /* ignore */
    }
    if (popup && !popup.closed) {
      try {
        popup.close();
      } catch {
        /* ignore */
      }
    }
    const msg = (await errorMessageFromAxios(e)) || e?.message || 'Unable to download the brochure.';
    window.alert(msg);
    return false;
  }
}
